import React, {FC, memo} from "react";
import moment from "moment";
import {CheckCircleOutlined, EuroCircleOutlined} from "@ant-design/icons";
import {green} from "@ant-design/colors";
import {Currency, GetBookingsByCourseIdWithCourseLessonBookingsQuery, PaymentType, UpdateCourseAndBookingsInput} from "../../../../generated/graphql";
import css from "./PaymentLists.less";
import {renderAmount} from "../../../../helpers/renderAmount";
import {useCurrency} from "../../../../helpers/currencyHelper";
import TotalPricePassive from "./TotalPricePassive";
import {DummySwitchMonthlyPayment} from "./SwitchMonthlyPayment";
import {colorGrey} from "../../../../styles/colors";
import classNames from "classnames";
import {getChangedPaymentIds} from "../helper";

type ScheduledPaymentType = NonNullable<GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0]["scheduledPayments"]>[number];

type PaymentsPassiveProps = {
    booking: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    values: UpdateCourseAndBookingsInput;
    changedPaymentIds?: {changedIds: Set<string>; addedIds: Set<string>; removedIds: Set<string>};
    isTotalPriceChanged?: boolean;
};

const PaymentsPassive: FC<PaymentsPassiveProps> = ({booking, values, changedPaymentIds, isTotalPriceChanged = false}) => {
    // Helper: Erstelle einen eindeutigen Key für eine Rate (muss mit helper.tsx übereinstimmen)
    const normalizeDate = (date: Date | string): number => {
        const d = typeof date === "string" ? new Date(date) : date;
        const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());

        return normalized.getTime();
    };

    const getPaymentKey = (payment: ScheduledPaymentType): string => {
        if (payment.id) {
            return payment.id;
        }
        const dueDate = normalizeDate(payment.paymentDue);
        const amount = Math.round((payment.paymentAmount ?? 0) * 100) / 100;

        return `${dueDate}-${amount}`;
    };

    const RenderMonthlyPaymentEntry = ({rate, index}: {rate: ScheduledPaymentType; index: number}) => {
        const currency = useCurrency();
        const paymentKey = getPaymentKey(rate);
        const isChanged = changedPaymentIds?.changedIds.has(paymentKey) || changedPaymentIds?.removedIds.has(paymentKey);

        return (
            <div key={rate.paymentDue} className={classNames(css.monthlyPaymentEntry, isChanged && css.changedPayment)}>
                {/* <div className={css.main}> */}
                <div className={rate.paymentDone ? css.mainDone : css.mainDue}>
                    <div className={css.month}>
                        <span className={css.num}>{`${index + 1}.`}</span>
                        <span>{moment(rate.paymentDue).format("MMM YY")}</span>
                    </div>
                    {rate.paymentDone ? (
                        <div className={css.paymentDoneContainer}>
                            <div className={css.paymentDone}>{`Bezahlt am ${moment(rate.paymentDone).format("DD.MM.YYYY")}`}</div>
                            <CheckCircleOutlined style={{color: green[5], marginTop: -2, /* paddingRight: 4, */ fontSize: 13}} />
                        </div>
                    ) : (
                        <div className={css.paymentDueContainer}>
                            <div className={css.paymentDue}>{`Fällig am ${moment(rate.paymentDue).format("DD.MM.YYYY")}`}</div>
                            <EuroCircleOutlined style={{color: colorGrey, /* marginTop: -2, */ fontSize: 13}} />
                        </div>
                    )}
                    <div className={css.amount}>
                        {renderAmount({
                            amount: rate.paymentAmount ?? 0,
                            currency: currency.short as Currency,
                        })}
                    </div>
                </div>
                {/* {renderHint()} */}
            </div>
        );
    };

    return (
        <div>
            <DummySwitchMonthlyPayment paymentType={booking.paymentType as PaymentType} allowUpFrontPayment={true} allowMonthlyPayment={!values.allowMolliePayment} />
            {booking.scheduledPayments?.map((rate, index) => {
                return <RenderMonthlyPaymentEntry key={rate.id || `payment-${index}`} rate={rate} index={index} />;
            })}
            <TotalPricePassive booking={booking} values={values} isChanged={isTotalPriceChanged} />
        </div>
    );
};

export default memo(PaymentsPassive);
